import type { IntakeConfig, ShopBranding } from "@/lib/intake/intake-config"

// Backend DTO types — derived from NestJS entities

export interface AccessTokenResponse {
  accessToken: string
}

export interface MessageResponse {
  message: string
}

export interface SignUpRequest {
  email: string
  password: string
  firstName?: string
  lastName?: string
}

export interface SignInRequest {
  email: string
  password: string
}

export interface ResetPasswordRequest {
  token: string
  newPassword: string
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface SetPasswordRequest {
  password: string
}

export interface UpdateProfileRequest {
  firstName?: string
  lastName?: string
}

// Wire shape returned by the backend for User entities (PATCH /auth/profile,
// nested in TeamUser.user, invite.user, invite.inviter, etc).
//
// NOTE: there is no `admin` field on this shape. The backend's User entity
// declares `admin` as `hidden: true`, so the serializer strips it before
// it goes out the door. Read super-admin status from the JWT's `isAdmin`
// claim instead.
export interface ApiUser {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  isActive: boolean
  hasSetPassword?: boolean
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

/** Returned by GET /auth/profile — basic user plus the user's team memberships. */
export interface ApiUserProfile extends ApiUser {
  teams: ApiTeam[]
}

/** Returned by GET /v1/users/me/memberships — slim per-team summary used by the
 *  invitee welcome gate. `welcomedAt: null` is the gate signal; legacy rows
 *  surface as their createdAt so existing users don't see the welcome flow. */
export interface ApiMembership {
  teamId: number
  role: string
  welcomedAt: string | null
}

export type TeamType = 'DealershipGroup' | 'Dealership' | 'Bodyshop'

export interface CreateTeamRequest {
  name: string
  type: TeamType
  street?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  phoneNumber?: string
  // Role the team creator should be set up as on the new team's `teamUsers`
  // record (Title Case, e.g. "General Manager"). Maps to BE
  // `CreateTeamDto.ownerRole`; lets the user pick from the role list of
  // the chosen org type so nav/permissions/dashboards reflect their job.
  ownerRole?: string
}

export interface UpdateTeamRequest {
  name?: string
  street?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  phoneNumber?: string
}

// PUT /v1/teams/{teamId}/settings — UpdateTeamSettingsDto. Owns address fields
// (mirrors UpdateTeamRequest minus `name`) plus a fixed-shape metadata block.
//
// NOTE on `materials`: the OpenAPI spec's BodyshopMaterialsDto only declares
// `paintMaterials`, but the Fees tab UI (fees-settings.tsx) also sends
// `bodyMaterials`. Today NestJS's default validation pipe accepts the extra
// field; if the BE ever flips `forbidNonWhitelisted: true`, `bodyMaterials`
// will start 400-ing and either the spec or the UI needs to move. Kept here
// to match what the FE actually sends.
export interface BodyshopLaborRate {
  bodyLabor?: number
  paintLabor?: number
  frameLabor?: number
  mechanical?: number
  aluminumBody?: number
  aluminumFrame?: number
}
export interface BodyshopMaterials {
  bodyMaterials?: number
  paintMaterials?: number
}
export interface BodyshopExtraFees {
  insideStorage?: number
  outsideStorage?: number
}
export interface BodyshopVehicleScans {
  preRepairScan?: number
  postRepairScan?: number
}

// Dealership service-lane baseline. Set at launch (collaboratively with the
// eTX onboarding team) and rarely changed after. Drives every downstream
// calculation: the suggested weekly goal, the $-denominated gap on the
// dashboard, the projected gross profit shown in emails, etc.
//
// Industry defaults (~40% visible-damage rate, $5K avg RO, 5% conversion) are
// applied at hydration time so a brand-new dealership can still see plausible
// projections before they've filled the form in.
export interface ServiceLaneBaseline {
  dailyLaneVolume?: number
  visibleDamagePct?: number
  avgRoValue?: number
  targetConversionRate?: number
  // Manager-set override for the number of advisors in the lane. Falls back
  // to the live count derived from team membership when absent. Stored
  // explicitly because the inferred count can lag (recent hires/departures)
  // and the calculator's projected per-advisor income is sensitive to it.
  activeAdvisors?: number
}

// Dealership pay & reward configuration. ServiceManager-owned.
//
// Core pay model (always on by default):
//   • `perEstimateSubmitted` — the advisor's guaranteed slice for writing
//     an estimate. Builds the habit by certainty, not by chase.
//   • `perEstimateCompleted` — fires when the body shop transitions the
//     estimate to Completed. Ties advisor compensation to actual revenue.
//
// Bonuses (opt-in, off by default — "cherry on top"):
//   • Activation milestones (first estimate, 5-day, 30-day streaks).
//   • Weekly/monthly goal-hit bonuses.
//   • Streak bonus (4-week run).
//   • Leaderboard prize.
//
// Targets (`weeklyEstimateTarget` / `monthlyEstimateTarget`) are only used
// when the matching bonus trigger is enabled — the dashboard never displays
// goal-progress UI for a bonus the manager hasn't turned on.
export interface DealershipRewardBundle {
  badge?: { enabled: boolean; label?: string }
  monetary?: { enabled: boolean; amount?: number }
}

export interface DealershipLeaderboardBundle {
  scope?: "top_1" | "top_3" | "exceed_goal_pct"
  exceedGoalPct?: number
  badge?: { enabled: boolean; label?: string }
  monetary?: { enabled: boolean; amount?: number }
}

// Reward trigger keys. Each maps to a specific behavioral event the platform
// fires automatically. Settings UI is opinionated about defaults so a
// ServiceManager doesn't stare at blank fields.
export type DealershipRewardTrigger =
  // Core pay model — fires per estimate, regardless of streak/goal state.
  | "perEstimateSubmitted"
  | "perEstimateCompleted"
  // Activation — career milestones. Fire once per advisor in their lifetime.
  | "activationFirstEstimate"
  | "activation5DayStreak"
  | "activation30DayStreak"
  // Achievement — recurring goal-hit rewards.
  | "weeklyGoalHit"
  | "monthlyGoalHit"
  | "weeklyStreak4Bonus"

// When the dealership settles advisor pay. Drives the dashboard's
// "next payout" countdown and the available-vs-pending wallet split.
export type PayoutCadence = "weekly" | "biweekly" | "monthly"

// Competition modules — the optional gamification layer on top of the pay
// model and bonuses. A "valid scan" is an estimate record: each estimate
// the advisor creates counts as one scan. Same keyed-map convention as
// `rewards`: absent key = module disabled, unknown keys round-trip.
export type CompetitionModuleKey =
  | "dailyScanChampion" | "firstScanOfDay" | "hotStreak" | "fastStart"        // daily
  | "weeklyVolumeLeaderboard" | "comebackChallenge" | "beatPersonalBest"      // weekly
  | "streakBonus" | "teamGoal"                                                // streak / team

export interface CompetitionModuleConfig {
  enabled: boolean
  amount: number                                   // $ prize
  threshold?: number                               // hotStreak scans-in-a-day, fastStart first-to-N target, teamGoal collective target
  streakDays?: 5 | 10 | 30                         // streakBonus consecutive workdays
  qualifier?: "top_1" | "top_3" | "above_target"   // weeklyVolumeLeaderboard
  window?: "weekly" | "monthly"                    // teamGoal
}

export interface DealershipGoalsRewards {
  weeklyEstimateTarget?: number
  monthlyEstimateTarget?: number
  // Optional per-advisor target overrides, keyed by user id (string). Absent
  // key = that advisor uses the global target ÷ headcount. Lets a manager set
  // a different bar for a standout (or ramping) advisor without moving the
  // team goal. (Settings UI: goals & rewards.)
  perAdvisorGoals?: Record<
    string,
    { weeklyEstimateTarget?: number; monthlyEstimateTarget?: number }
  >
  // Average vehicles through the service lane per business day — the dealer
  // provides this ("they have to tell us how many cars come through", CEO,
  // Jul 14). Denominator for the dashboard's lane-capture rate; optional —
  // the capture card hides itself when unset. Settings UI: follow-up.
  avgDailyLaneROs?: number
  // Map of reward bundles keyed by trigger. Absent key = trigger disabled.
  // Storing as a map rather than fixed fields lets us add new triggers
  // without breaking older saved data — unknown keys round-trip untouched.
  rewards?: Partial<Record<DealershipRewardTrigger, DealershipRewardBundle>>
  // Monthly leaderboard is its own shape (has scope + threshold). Kept on
  // the parent rather than in `rewards` because the LeaderboardBundle has
  // extra fields beyond the standard badge/monetary/points trio.
  leaderboardReward?: DealershipLeaderboardBundle
  // How often advisors are paid out. Drives the dashboard's "next payout
  // in X days" + the split between settled (available) and unsettled
  // (pending) wallet balances.
  payoutCadence?: PayoutCadence
  // Competition modules keyed by module. Absent key = module disabled —
  // same forward-compat rationale as `rewards`.
  competitions?: Partial<Record<CompetitionModuleKey, CompetitionModuleConfig>>
  // Whether payout periods need manager sign-off ("manual") or auto-approve
  // at the computed amounts ("auto"). Absent = manual.
  payoutMode?: "manual" | "auto"
}

export interface MarketingSuperchargeConfig {
  enabled: boolean
  // Graduation lever (default off): let "looks good" estimates auto-approve
  // once a shop's AI confidence proves out.
  autoApproveClean?: boolean
  enabledAt?: string | null
  // Compliance: automated messages only leave inside this window (defaults
  // 8 → 20, TCPA-safe). Anything outside queues for the next morning.
  quietStartHour?: number
  quietEndHour?: number
}

export interface TeamSettingsMetadata {
  laborRate?: BodyshopLaborRate
  /** Consumer intake (public /go /embed /kiosk surfaces) — config + shop brand.
   *  Saved through the real PUT /teams/{id}/settings; the public read is the
   *  mock GET /public/shops/:id. Schema: src/lib/intake/intake-config.ts. */
  intake?: IntakeConfig
  branding?: ShopBranding
  materials?: BodyshopMaterials
  extraFees?: BodyshopExtraFees
  vehicleScans?: BodyshopVehicleScans
  serviceLane?: ServiceLaneBaseline
  goalsRewards?: DealershipGoalsRewards
  marketingSupercharge?: MarketingSuperchargeConfig
  /** The numbers behind the task formula (mock-only; see design log). */
  followUpCadence?: { callEveryDays: number; maxCalls: number }
  /** Booking-calendar working hours + rules (mock-only feature; defaults apply
   *  when absent — see booking-calendar-settings.tsx). */
  scheduling?: {
    openDays?: number[]
    startHour?: number
    endHour?: number
    slotMinutes?: number
    capacityPerSlot?: number
    leadTimeHours?: number
    windowDays?: number
    blockedDates?: string[]
    inspectionEnabled?: boolean
    dropoffEnabled?: boolean
    /** Day-before appointment reminder text (quiet-hours clamped). */
    remindersEnabled?: boolean
  }
}

export interface UpdateTeamSettingsRequest {
  street?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  phoneNumber?: string
  metadata?: TeamSettingsMetadata
}

export interface ApiTeam {
  id: number
  name: string
  type: TeamType
  street: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  country: string | null
  phoneNumber: string | null
  metadata: Record<string, unknown> | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface InviteMemberRequest {
  email: string
  role: string
  department?: string
}

export type TeamMemberInviteStatus = 'active' | 'paused' | 'revoked' | 'accepted'

export interface ApiTeamMemberInvite {
  id: number
  uuid: string
  user: ApiUser
  team: ApiTeam
  inviter: ApiUser
  role: string
  department: string | null
  status: TeamMemberInviteStatus
  isActive: boolean
  createdAt: string
  updatedAt: string
  // Set by the BE every time the invite email goes out (initial send + each
  // resend). Optional because the OpenAPI schema doesn't list it as required
  // and very old invites may pre-date the column. Used by the resend flow to
  // show "last sent X ago" hints; absence ≈ never sent.
  lastSentAt?: string | null
}

// Slim response from GET /v1/public/invites/:uuid — intentionally minimal
// (no user record, no team id/type, no inviter email) to keep the public
// surface low-trust. See etx/server/src/team/public-invites.controller.ts.
export interface PublicInviteResponse {
  uuid: string
  email: string
  role: string
  department?: string
  status: TeamMemberInviteStatus
  metadata?: unknown
  inviter: { firstName: string; lastName: string }
  team: { name: string }
}

// Response from POST /v1/public/invites/:uuid — the controller catches all
// errors and returns 201 with status='error' rather than throwing, so the
// FE must inspect status, not HTTP code. token is only present for newly
// auto-created users (hasSetPassword=false).
export interface AcceptInviteResponse {
  token?: string | null
  status: 'success' | 'error'
}

export interface DealershipGroupResponse {
  id: number
  group: ApiTeam
  team: ApiTeam
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface AddDealershipRequest {
  dealershipName: string
  generalManagerEmail?: string
  // Optional location for the dealership team. The group's onboarding flow
  // collects city/state per row so the dealership cards in switchers + the
  // org chart aren't anonymous strings.
  city?: string
  state?: string
}

export interface ConnectBodyshopRequest {
  // BE links by name: if a body shop already exists with this name, it's
  // reused; otherwise a new one is created and `generalManagerEmail` is
  // required. Sharing one body shop across multiple dealerships means
  // sending the same `bodyshopName` for each connect call.
  bodyshopName: string
  generalManagerEmail?: string
}

// DELETE /v1/teams/{teamId}/bodyshop — DELETE-with-body. The bodyshopId is
// required so a stale client can't accidentally disconnect a bodyshop that
// has been re-linked since the last GET.
export interface DisconnectBodyshopRequest {
  bodyshopId: number
}

// GET /v1/teams/{teamId}/bodyshop returns the connected bodyshop team, or 404 when none.
export type ConnectedBodyshopResponse = ApiTeam

// GET /v1/teams/{teamId}/members — slim per-member shape for the Team Members UI.
// Mirrors etx/server's TeamMemberSummaryDto. `id` is the user id, required for
// PATCH/DELETE /members/:userId.
export interface TeamMemberSummary {
  id: number
  firstName: string
  lastName: string
  email: string
  role: string
  /** The user's department (Service / Sales / Body Shop / Management) —
   *  shown under name · role wherever an author is picked or listed. */
  department: string | null
}

export interface UpdateTeamMemberRoleRequest {
  role: string
}

// ── Vehicle & Estimate Enums ──

// 'Confirmed' — body shop confirmed the estimate, outreach not started yet
// (sits between Pending and InProgress; deployed on real BE 2026-07-13).
export type EstimateStatus = 'Draft' | 'Loading' | 'Pending' | 'Confirmed' | 'InProgress' | 'Scheduled' | 'Completed' | 'Declined'

export type VehicleType =
  | 'Sedan' | 'Hatchback' | 'Coupe' | 'Convertible' | 'StationWagon'
  | 'SUV' | 'Crossover' | 'MiniVan' | 'CityCar' | 'Compact'
  | 'SuperMini' | 'Pickup' | 'Van' | 'CargoVan' | 'Other'

export type VehicleSide =
  | 'FRONT_LEFT_SIDE' | 'FRONT_RIGHT_SIDE' | 'FRONT_LEFT_CORNER' | 'FRONT_RIGHT_CORNER'
  | 'REAR_LEFT_SIDE' | 'REAR_RIGHT_SIDE' | 'REAR_LEFT_CORNER' | 'REAR_RIGHT_CORNER'
  | 'FRONT_CENTER' | 'REAR_CENTER' | 'LEFT_SIDE' | 'RIGHT_SIDE' | 'ROOF'

export type VehicleSection =
  | 'FRONT_BUMPER_GRILLE' | 'REAR_BUMPER' | 'FENDER' | 'FRONT_DOOR' | 'REAR_DOOR'
  | 'HOOD' | 'TRUNK' | 'ROOF' | 'FRONT_LAMPS' | 'REAR_LAMPS' | 'QUARTER_PANEL'
  | 'ROCKER_PANEL' | 'LIFT_GATE' | 'TAIL_GATE' | 'WHEELS' | 'FRAME' | 'TRUNK_LID'
  | 'PICKUP_BOX' | 'WINDSHIELD'

// Mirror of the BE `UpdateEstimateLineItemRowDto.component` enum (in
// `vehicle-section-from-component.ts`). Used by the full-estimate
// line-item editor to constrain the per-row component picker.
export type VehicleComponent =
  | 'FRONT_BUMPER_COVER' | 'FRONT_IMPACT_BAR' | 'FRONT_ENERGY_ABSORBER' | 'FRONT_LOWER_COVER'
  | 'FRONT_GRILLE_ASSEMBLY' | 'FRONT_UPPER_GRILLE' | 'FRONT_LOWER_GRILLE' | 'FRONT_PARK_SENSORS'
  | 'FRONT_CAMERA' | 'REAR_BUMPER_COVER' | 'REAR_IMPACT_BAR' | 'REAR_ENERGY_ABSORBER'
  | 'REAR_LOWER_COVER' | 'REAR_BODY_PANEL' | 'REAR_PARK_SENSORS'
  | 'LT_FENDER' | 'RT_FENDER' | 'LF_FENDER_LINER' | 'RT_FENDER_LINER'
  | 'LF_WHEEL_OPENING_MOLDING' | 'RF_WHEEL_OPENING_MOLDING' | 'LR_WHEEL_OPENING_MOLDING' | 'RR_WHEEL_OPENING_MOLDING'
  | 'LT_FRONT_DOOR_SHELL' | 'RT_FRONT_DOOR_SHELL' | 'LT_DOOR_MIRROR' | 'RT_DOOR_MIRROR'
  | 'LF_DOOR_HANDLE' | 'RF_DOOR_HANDLE' | 'LF_DOOR_MOLDING' | 'RF_DOOR_MOLDING'
  | 'LT_REAR_DOOR_SHELL' | 'RT_REAR_DOOR_SHELL' | 'LR_DOOR_HANDLE' | 'RR_DOOR_HANDLE'
  | 'RR_DOOR_MOLDING' | 'LR_DOOR_MOLDING'
  | 'LT_HEADLAMP_ASSEMBLY' | 'RT_HEADLAMP_ASSEMBLY' | 'LT_FOG_LAMP' | 'RT_FOG_LAMP'
  | 'RT_TURN_SIGNAL_LAMP' | 'LT_TURN_SIGNAL_LAMP' | 'LT_TAIL_LAMP_ASSEMBLY' | 'RT_TAIL_LAMP_ASSEMBLY'
  | 'LT_TAIL_LAMP_POCKET' | 'RT_TAIL_LAMP_POCKET' | 'LT_BACKUP_LAMP' | 'RT_BACKUP_LAMP'
  | 'LT_QUARTER_PANEL' | 'RT_QUARTER_PANEL' | 'LT_OUTER_PANEL' | 'RT_OUTER_PANEL'
  | 'LT_SIDE_PANEL' | 'RT_SIDE_PANEL' | 'LT_QUARTER_GLASS' | 'RT_QUARTER_GLASS'
  | 'LT_ROCKER_PANEL' | 'RT_ROCKER_PANEL' | 'LT_ROCKER_MOLDING' | 'RT_ROCKER_MOLDING'
  | 'REAR_LIFTGATE' | 'REAR_GLASS' | 'BACK_UP_CAMERA' | 'REAR_TAILGATE' | 'TAILGATE_MOLDING'
  | 'LT_FRONT_WHEEL_RIM' | 'RT_FRONT_WHEEL_RIM' | 'LT_REAR_WHEEL_RIM' | 'RT_REAR_WHEEL_RIM'
  | 'LT_FRONT_FRAME_RAIL' | 'RT_FRONT_FRAME_RAIL' | 'LT_REAR_FRAME_RAIL' | 'RT_REAR_FRAME_RAIL'
  | 'TRUNK_LID' | 'REAR_SPOILER' | 'ROOF_PANEL' | 'SUN_ROOF' | 'REAR_CAMERA'
  | 'RT_BEDSIDE_SIDE_ASY' | 'LT_BEDSIDE_SIDE_ASY' | 'LT_UPPER_MOLDING' | 'RT_UPPER_MOLDING'
  | 'RT_WHEEL_OPENING_MOLDING_PICKUP' | 'LT_WHEEL_OPENING_MOLDING_PICKUP'
  | 'HOOD' | 'WINDSHIELD_GLASS'

// PUT /v1/estimates/{estimateId}/line-items DTO. Action is upper-case on
// the wire even though `ApiEstimateLineItem.action` reads back as title-case
// ("Replace" / "Repair") — BE normalizes on write.
export type LineItemAction = 'REPLACE' | 'REPAIR'

/** A photo that shows a line item, with an optional marker on it.
 *  `x`/`y` are normalised 0–1 from the top-left; null = "shows it, no mark".
 *  `by: 'ai'` links come from the damage agent and sit as "AI found" until a
 *  person confirms. MOCK-ONLY today: stored at `lineItem.metadata.photos`;
 *  the real BE has no image ↔ line-item link yet (flagged for the dev). */
export interface PhotoLink {
  imageId: number
  x: number | null
  y: number | null
  by: 'ai' | 'user'
  confirmed: boolean
}

export interface UpdateEstimateLineItemRowDto {
  /** Present when editing an existing row; omit to create a new one. */
  id?: number
  /** The photos this line appears on (mock-only field, see PhotoLink). */
  photos?: PhotoLink[]
  /** Who put the line on the estimate (mock-only; read back from
   *  `lineItem.metadata.addedBy`, missing = 'ai'). */
  addedBy?: 'ai' | 'user'
  component: VehicleComponent
  description?: string
  action: LineItemAction
  side: VehicleSide
  partType: string
  /** Required by BE. UI hides the field on the polished editor; manual
   *  rows default to 5 (medium) before the PUT. */
  severity: number
}

export interface UpdateLineItemsRequest {
  lineItems: UpdateEstimateLineItemRowDto[]
}

// ── Vehicle & Customer Entities ──

export interface ApiCustomer {
  id: number
  name: string
  email: string | null
  phone: string | null
  bodyshop?: ApiTeam
  dealership?: ApiTeam
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface ApiVehicle {
  id: number
  vin: string
  make: string | null
  model: string | null
  year: string | null
  trim: string | null
  type: VehicleType | null
  color: string | null
  bodyshop?: ApiTeam
  dealership?: ApiTeam
  metadata: Record<string, unknown>
  customer?: ApiCustomer
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// ── Estimate Entities ──

export interface ApiEstimateImage {
  id: number
  src: string
  side: VehicleSide
  quality: number
  description: string | null
  metadata: Record<string, unknown>
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Operations are children of line items (BE entity:
// EstimateLineItemOperation, OneToMany on EstimateLineItem). Populated
// by `runSectionsOrchestrator` on the BE; the FE displays them
// read-only in the full-estimate Operations review step.
export type SectionWorkType =
  | 'body_repair' | 'refinish' | 'refinish_prep' | 'refinish_adder'
  | 'adas_diagnostic' | 'mechanical' | 'shop_admin' | 'sublet_other'

export type OperationConfidence = 'high' | 'medium' | 'low'

export interface ApiOperation {
  id?: number
  description: string
  workType: SectionWorkType
  causalRole?: string
  triggeredBy?: string
  laborHours: number
  partPrice: number
  subletAmount?: number | null
  confidence?: OperationConfidence
  confidenceNote?: string | null
  userGenerated?: boolean
}

export interface ApiEstimateLineItem {
  id: number
  component: string
  section: VehicleSection
  description: string | null
  action: string | null
  confidence: string | null
  side: VehicleSide | null
  partType: string | null
  min: number | null
  max: number | null
  avg: number | null
  manHours: number
  severity: number | null
  damageExtent: string | null
  structuralInvolvement: boolean | null
  material: string | null
  adjacentComponentInvolvement: boolean | null
  metadata: Record<string, unknown> | null
  /** Children rows produced by the sections-orchestrator. Empty until
   *  the orchestrator runs. Read-only on the FE. */
  operations?: ApiOperation[]
  /** Virtual field — BE re-derives this each time the entity is hydrated
   *  (`@Property({ persist: false })` + `@OnLoad()`), so the value on the
   *  wire is always fresh. Sum of `(partPrice + subletAmount)` across the
   *  line item's operations; labor hours are excluded by design (they
   *  fold into the estimate-level total via `getFinalEstimatedCost`). */
  totalCost: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface ApiEstimateAuthor {
  id: number
  firstName: string
  lastName: string
}

export interface ApiEstimate {
  id: number
  vehicle: ApiVehicle
  description: string | null
  scheduledAt: string | null
  images: ApiEstimateImage[]
  context: Record<string, unknown> | null
  lineItems: ApiEstimateLineItem[]
  reportFileUrl: string | null
  min: number
  max: number
  avg: number
  notes: string | null
  status: EstimateStatus
  totalLoss: boolean | null
  bodyshop?: ApiTeam
  dealership?: ApiTeam
  // Service advisor who originated the estimate (the user authenticated on
  // the POST /v1/estimates/team/:teamId/full call). Required for advisor-
  // level dashboards, streak counts, monthly leaderboards, and reward
  // attribution. Real BE: needs to surface User reference on Estimate; mock
  // backend stores the auth context user ID at create time.
  createdBy?: ApiEstimateAuthor | null
  metadata: Record<string, unknown>
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// ── Sales Lead (total-loss funnel) ──
//
// Mirrors etx/server SalesLead entity. Created as a side effect of the
// bodyshop's POST /v1/estimates/:id/total-loss; lives under
// /v1/sales/:teamId/leads/* on the real BE.
//
// Population caveat: the list endpoints (findAll/findPending/findAssigned/
// search) on the real BE do NOT call em.populate() — relations arrive as
// bare values, in any of three shapes depending on how the serializer
// handled the entity reference:
//   - just the FK number (e.g. `estimate: 5`)
//   - a single-key id object (e.g. `estimate: { id: 5 }`)
//   - or, if populated, the full nested entity
// Only `findOne` reliably returns the full populated entity. The helpers
// below normalize all three shapes so call sites don't have to branch.

export type SalesLeadDepartment = 'new' | 'used'

/** A relation on a SalesLead may be: a raw FK number, a `{id}` ref, the
 *  fully-populated entity, or null/undefined when absent. */
export type SalesLeadRef<T> = T | { id: number } | number | null | undefined

export interface ApiSalesLead {
  id: number
  email: string
  name: string
  phoneNumber: string
  estimate?: SalesLeadRef<ApiEstimate>
  dealership: SalesLeadRef<ApiTeam>
  department: SalesLeadDepartment
  salesManager?: SalesLeadRef<ApiUser>
  salesRep?: SalesLeadRef<ApiUser>
  scheduledAt: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface MarkTotalLossRequest {
  dealershipId?: number
  department?: SalesLeadDepartment
  salesManagerId?: number
}

export interface AssignSalesManagerRequest {
  salesManagerId: number
}

export interface AssignSalesRepRequest {
  salesRepId: number
}

export interface ChangeDepartmentRequest {
  department: SalesLeadDepartment
  salesManagerId?: number
  salesRepId?: number
}

export interface SearchSalesLeadRequest {
  searchTerm: string
}

/** Type guard: is the ref a populated object (not a primitive number, null,
 *  or undefined)? Required because the wire shape can hand back any of
 *  those for an unpopulated relation, and `in`/property access blow up on
 *  primitives. */
function isPopulated<T>(ref: SalesLeadRef<T>): ref is T | { id: number } {
  return ref != null && typeof ref === 'object'
}

/** Resolve the customer-facing name for a sales lead. SalesLead has its
 *  own `name` field (snapshotted from the estimate's customer at creation),
 *  so this never needs to traverse the estimate graph. */
export function salesLeadCustomerName(lead: ApiSalesLead | null | undefined): string {
  return lead?.name?.trim() || 'Unknown Customer'
}

/** Vehicle display string when the estimate is populated; otherwise '—'.
 *  Real BE list endpoints don't populate `estimate`, so callers should
 *  expect this to often be '—' until the BE adds populate to those paths. */
export function salesLeadVehicleName(lead: ApiSalesLead | null | undefined): string {
  const est = lead?.estimate
  if (!isPopulated(est) || !('vehicle' in est) || !est.vehicle) return '—'
  return vehicleDisplayName(est.vehicle)
}

/** VIN when the estimate.vehicle is populated; otherwise null. */
export function salesLeadVin(lead: ApiSalesLead | null | undefined): string | null {
  const est = lead?.estimate
  if (!isPopulated(est) || !('vehicle' in est) || !est.vehicle?.vin) return null
  return est.vehicle.vin
}

/** Estimate value when the estimate is populated; otherwise null. */
export function salesLeadEstimateValue(lead: ApiSalesLead | null | undefined): number | null {
  const est = lead?.estimate
  if (!isPopulated(est) || !('avg' in est) || typeof est.avg !== 'number') return null
  return est.avg
}

/** Display name for a User reference on a sales lead. Falls back from
 *  firstName+lastName → email → '—' as the wire shape degrades from full
 *  user → bare ref → primitive id. */
export function salesLeadUserName(ref: SalesLeadRef<ApiUser>): string {
  if (!isPopulated(ref)) return '—'
  if ('firstName' in ref || 'lastName' in ref || 'email' in ref) {
    const u = ref as ApiUser
    const composed = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()
    return composed || u.email || '—'
  }
  return '—'
}

/** ID extractor for a possibly-bare reference. Handles all three shapes:
 *  primitive number → returns it; `{id}` ref → returns ref.id; object with
 *  numeric id field → returns it; null/undefined/anything else → null. */
export function refId<T extends { id: number }>(
  ref: SalesLeadRef<T>,
): number | null {
  if (ref == null) return null
  if (typeof ref === 'number') return Number.isFinite(ref) ? ref : null
  if (typeof ref === 'object' && 'id' in ref && typeof ref.id === 'number') return ref.id
  return null
}

// ── Estimate DTOs ──

export interface StartEstimateCustomerDto {
  name: string
  email?: string
  phone?: string
}

export interface StartEstimateVehicleDto {
  vin: string
  make?: string
  model?: string
  year?: string
  trim?: string
  type?: VehicleType
  metadata?: Record<string, unknown>
}

export interface StartEstimateRequest {
  customer: StartEstimateCustomerDto
  vehicle: StartEstimateVehicleDto
}

export interface UpdateEstimateStatusRequest {
  status: Exclude<EstimateStatus, 'Loading'>
}

export interface AddEstimateImageDto {
  imageUrl: string
  side: VehicleSide
  qualityScore: number
  qualified: boolean
  description: string
}

export interface AddEstimateImagesRequest {
  images: AddEstimateImageDto[]
}

export interface EstimateDamageRequest {
  quickEstimateMode?: boolean
}

export interface EstimateMajorOperationsRequest {
  quickEstimateMode?: boolean
}

export interface EstimateListParams {
  teamId: number | string
  limit?: number
  offset?: number
}

// ── Reporting ──
//
// GET /v1/reporting/estimates/team/:teamId — server-side aggregation of estimates
// scoped to the requested team (dealership, dealership group, or bodyshop). The
// per-user breakdowns are empty for bodyshop teams; for groups they roll up
// across every child dealership.
//
// `completionRate` arrives as a stringified percentage with one decimal (e.g.
// "85.7"); the FE converts to a number when it needs to compare or render.

export interface EstimatesReportPerUserRow {
  userId: number
  name: string
  totalEstimates: number
  totalRevenue: number
  avgRevenue: number
  completionRate: string
}

// A single field-collection walkaround record — one vehicle looked at in the
// lane, whether or not it turned into a damage estimate. `estimateId` links the
// walkarounds that found damage; the ones without it are the denominator behind
// "N vehicles evaluated → M estimates → damage rate".
//
// Real BE caveat: the row-74 report does NOT surface walkarounds today (the
// endpoint is unreviewed — tracking.md row 74). This field is a mock-only
// addition to the contract so the room can tell the CEO's "1 in 3" story; the
// dev must reconcile the real shape (and whether walkarounds are recorded at
// all) before wiring production. See the design log.
export interface EstimatesReportWalkaround {
  id: number
  vehicleId: number | null
  source: string
  dealershipId: number | null
  advisorId: number | null
  estimateId: number | null
  createdAt: string
}

export interface EstimatesReport {
  totalRevenue: number
  totalEstimates: number
  avgValue: number
  completionRate: string
  perServiceAdvisor: EstimatesReportPerUserRow[]
  perServiceManager: EstimatesReportPerUserRow[]
  /** Mock-only addition — see EstimatesReportWalkaround. Absent on real BE. */
  walkarounds?: EstimatesReportWalkaround[]
}

// ── Helpers ──

/** Get display name for vehicle from ApiVehicle */
export function vehicleDisplayName(v: ApiVehicle | null | undefined): string {
  if (!v) return 'Unknown Vehicle'
  return [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ') || v.vin
}

/** Get customer name from estimate */
export function estimateCustomerName(est: ApiEstimate | null | undefined): string {
  return est?.vehicle?.customer?.name ?? 'Unknown Customer'
}

/** Get customer email from estimate */
export function estimateCustomerEmail(est: ApiEstimate | null | undefined): string | null {
  return est?.vehicle?.customer?.email ?? null
}

/** Get customer phone from estimate */
export function estimateCustomerPhone(est: ApiEstimate | null | undefined): string | null {
  return est?.vehicle?.customer?.phone ?? null
}
